/*
 * StrataGreen Seymour chatbot widget — Shopify storefront embed.
 *
 * Reads config from #seymour-chat-root[data-config]. Builds the launcher
 * button + slide-out panel. Streams chat via SSE. When the bot calls
 * add_to_cart, this widget calls the Shopify Ajax cart API directly so
 * the item lands in the customer's actual storefront cart.
 */
(function () {
  "use strict";

  // ------------------- config -------------------
  const rootEl = document.getElementById("seymour-chat-root");
  if (!rootEl) return;
  let CFG;
  try {
    CFG = JSON.parse(rootEl.dataset.config || "{}");
  } catch (e) {
    console.error("[seymour] bad config JSON", e);
    return;
  }
  const API_BASE = CFG.apiBase || "https://next.stratacorporation.net.au";
  const SHOPIFY_NATIVE = !!CFG.shopifyNative;
  const TOKEN_KEY = "seymour:token";
  const SESSION_KEY = "seymour:session";
  const OPEN_KEY = "seymour:open";
  const HISTORY_KEY = "seymour:history";
  const LAST_CUSTOMER_KEY = "seymour:lastCustomer";
  const HISTORY_MAX = 60;
  // History format: [{kind:"user"|"bot"|"err"|"cards"|"cart_confirm", ...}, ...]
  // Entries are slimmed before storage (slimEntry below) so we don't
  // balloon localStorage with variant arrays, full product payloads etc.

  // Identity-change reset: clear session + history if the effective
  // account changed since we last rendered. Composite key includes both
  // the Shopify customer.id AND the Acumatica customer id (= Company.
  // external_id) so we also reset when a B2B contact switches companies
  // — Shopify lets a single customer act on behalf of multiple Companies
  // and the customer.id stays the same across that switch, but their
  // trade pricing / invoices / statements come from a different
  // Acumatica account. Both fields are HMAC-signed so we can trust them
  // as the source of "who is this customer effectively".
  const cust  = CFG.customerId           == null ? "" : String(CFG.customerId);
  const acu   = CFG.acumaticaCustomerId  == null ? "" : String(CFG.acumaticaCustomerId);
  const currentCustomer = cust + "|" + acu;
  const lastCustomer = localStorage.getItem(LAST_CUSTOMER_KEY) || "";
  if (currentCustomer !== lastCustomer) {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(HISTORY_KEY);
    localStorage.setItem(LAST_CUSTOMER_KEY, currentCustomer);
  }

  let sessionId = localStorage.getItem(SESSION_KEY) || null;
  let history = [];
  try {
    history = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
  } catch (e) {
    history = [];
  }

  // Strip each card/content entry to just what appendXxxCards needs for
  // replay. Stops localStorage from balloning with variant arrays, full
  // SEO descriptions etc — Safari's quota is 5MB.
  function slimCard(c) {
    if (!c) return c;
    return {
      title:             c.title,
      image_url:         c.image_url,
      online_store_url:  c.online_store_url,
      short_description: (c.short_description || "").slice(0, 200),
      price_range:       c.price_range,
      your_price_range:  c.your_price_range,
      tax_label:         c.tax_label,
      variant_count:     c.variant_count,
      stock_status:      c.stock_status || null,
    };
  }
  function slimContent(c) {
    if (!c) return c;
    return {
      type:             c.type,
      title:            c.title,
      summary:          (c.summary || "").slice(0, 200),
      online_store_url: c.online_store_url,
      image_url:        c.image_url,
      blog:             c.blog,
    };
  }
  function slimEntry(entry) {
    if (!entry) return entry;
    switch (entry.kind) {
      case "cards":
        return {
          kind: "cards",
          label: entry.label,
          products: (entry.products || []).map(slimCard),
        };
      case "content":
        return {
          kind: "content",
          items: (entry.items || []).map(slimContent),
        };
      default:
        return entry;
    }
  }

  function persistHistory() {
    if (history.length > HISTORY_MAX) history = history.slice(-HISTORY_MAX);
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch (e) {
      // Almost always QuotaExceededError. Halve the history and retry
      // — better to lose old turns than to lose persistence entirely.
      try {
        history = history.slice(-Math.floor(HISTORY_MAX / 2));
        localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
      } catch (_) { /* still over quota — drop silently */ }
    }
  }

  function recordEntry(entry) {
    history.push(slimEntry(entry));
    persistHistory();
  }

  // ------------------- DOM scaffold -------------------
  const avatarUrl = CFG.avatarUrl || "";
  const mascotUrl = CFG.mascotUrl || "";

  const launcher = document.createElement("button");
  launcher.className = "seymour-launcher";
  launcher.type = "button";
  launcher.setAttribute("aria-label", "Open chat with Seymour");
  // Both modes get the same HTML — CSS shows the mascot for floating
  // (standing Seymour on top of an "Ask Seymour" pill) and the round
  // avatar bust for inline (small icon inside the header pill).
  launcher.innerHTML = `
    ${mascotUrl ? `<img class="seymour-launcher-mascot" src="${mascotUrl}" alt="" aria-hidden="true">` : ""}
    <span class="seymour-launcher-avatar"${avatarUrl ? ` style="background-image:url('${avatarUrl}')"` : ""}></span>
    <span class="seymour-launcher-label">Ask Seymour</span>
    <span class="seymour-badge">BETA</span>
  `;
  document.body.appendChild(launcher);

  // Responsive launcher placement.
  //
  //   Desktop (≥ NARROW_BREAKPOINT and the storefront search icon is
  //   visible): inject the launcher as the next sibling of the search
  //   icon so it inherits the theme's sticky-header behaviour on scroll.
  //
  //   Mobile / narrow: fall back to the floating bottom-right round
  //   avatar — same UX as before the header link existed.
  const NARROW_BREAKPOINT = 900;   // px; below this, use floating mode

  function findSearchIcon() {
    return (
      document.querySelector('a[href*="/search"]:not([href*="search?"])') ||
      document.querySelector('[data-search-toggle], [data-search-open], [aria-controls*="search" i]') ||
      document.querySelector('button[aria-label*="search" i], a[aria-label*="search" i]') ||
      document.querySelector('.search-icon, .header__search, .header__icon--search')
    );
  }

  function isElementVisible(el) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return false;
    const cs = window.getComputedStyle(el);
    return cs.display !== "none" && cs.visibility !== "hidden";
  }

  function setMode(mode) {
    launcher.classList.remove("seymour-launcher--inline", "seymour-launcher--floating");
    launcher.classList.add("seymour-launcher--" + mode);
  }

  function findBackToTop() {
    return (
      document.querySelector('[data-back-to-top], .back-to-top, #back-to-top') ||
      document.querySelector('[aria-label*="back to top" i], [aria-label*="scroll to top" i]') ||
      document.querySelector('button.scroll-to-top, a.scroll-to-top')
    );
  }

  function placeLauncher() {
    const narrow = window.innerWidth < NARROW_BREAKPOINT;
    const searchEl = narrow ? null : findSearchIcon();
    if (!narrow && searchEl && isElementVisible(searchEl)) {
      // Inline next to the search icon. Insert AFTER it so it sits to
      // its right in normal LTR layouts.
      if (launcher.previousElementSibling !== searchEl) {
        searchEl.parentNode.insertBefore(launcher, searchEl.nextSibling);
      }
      setMode("inline");
      launcher.style.bottom = "";
    } else {
      // Floating bottom-right round avatar — needs to live on body.
      if (launcher.parentNode !== document.body) {
        document.body.appendChild(launcher);
      }
      setMode("floating");
      // Stack above the theme's "back to top" button if present + visible.
      // Re-evaluates on every placement (resize/scroll) because back-to-top
      // typically only appears once the user scrolls past a threshold.
      const btt = findBackToTop();
      if (btt && isElementVisible(btt)) {
        const r = btt.getBoundingClientRect();
        const fromBottom = window.innerHeight - r.top + 12;  // 12px gap above
        launcher.style.bottom = fromBottom + "px";
      } else {
        launcher.style.bottom = "";  // CSS default (20px)
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", placeLauncher, { once: true });
  } else {
    placeLauncher();
  }
  window.addEventListener("load", placeLauncher);
  window.addEventListener("resize", () => { placeLauncher(); placeNudge(); });
  // Back-to-top usually toggles on scroll — re-check, but throttled.
  let _placeQueued = false;
  window.addEventListener("scroll", () => {
    if (_placeQueued) return;
    _placeQueued = true;
    requestAnimationFrame(() => {
      _placeQueued = false;
      placeLauncher();
      placeNudge();
    });
  }, { passive: true });

  // ------------------- Nudge bubble -------------------
  // Friendly speech-bubble that drifts up out of the floating launcher
  // a few seconds after page load to encourage engagement. Pure
  // engagement nudge — doesn't pre-empt the chat itself. Dismissed by
  // clicking the × (cooldown), by clicking the launcher (one-off), or
  // automatically after a few seconds.
  const NUDGE_PROMPTS = [
    "G'day! Anything I can help you find?",
    "Need a hand finding the right product?",
    "<strong>Did you know?</strong> I can check live stock at every warehouse.",
    "<strong>Tip:</strong> Got a trade account? I can pull your pricing.",
    "Working on a project? Tell me what you're after — I'll dig it up.",
    "<strong>Did you know?</strong> I can email you any past invoice in seconds.",
    "Looking for a spec sheet or MSDS? Just ask 🌱",
    "<strong>Tip:</strong> I can quote freight to your job site right here.",
    "Want to track a recent order?",
    "<strong>Did you know?</strong> I'm here 24/7 — no waiting for business hours.",
  ];
  const NUDGE_DISMISS_KEY = "seymour:nudge:dismissed_at";
  const NUDGE_COOLDOWN_MS = 30 * 60 * 1000; // suppress for 30 min after × dismiss
  const NUDGE_INITIAL_DELAY_MS = 6000;       // first nudge appears 6s after page load
  const NUDGE_VISIBLE_MS = 12000;            // each nudge stays visible 12s
  const NUDGE_GAP_MS = 45000;                // gap between nudges (after hide)

  const nudge = document.createElement("div");
  nudge.className = "seymour-nudge";
  nudge.setAttribute("role", "status");
  nudge.innerHTML =
    '<button type="button" class="seymour-nudge-close" aria-label="Dismiss">×</button>' +
    '<span class="seymour-nudge-body"></span>';
  document.body.appendChild(nudge);
  const nudgeBody = nudge.querySelector(".seymour-nudge-body");
  const nudgeClose = nudge.querySelector(".seymour-nudge-close");

  let _nudgeHideTimer = null;
  let _nudgeNextTimer = null;
  let _nudgeShown = false;
  let _nudgeLastIdx = -1;
  let _nudgeStopped = false;       // hard stop: chat opened or × dismissed

  function placeNudge() {
    // Always anchor ABOVE the launcher with the tail pointing down.
    // Floating: bubble right-aligned with the round avatar, tail bottom-right.
    // Inline:   bubble left-aligned to the avatar INSIDE the pill, tail
    //           bottom-left — looks like speech coming from Seymour's face.
    const isInline   = launcher.classList.contains("seymour-launcher--inline");
    const isFloating = launcher.classList.contains("seymour-launcher--floating");
    if (!isInline && !isFloating) {
      nudge.classList.remove("open");
      return;
    }
    const r = launcher.getBoundingClientRect();
    // Bottom of the bubble sits 12px above the launcher's top edge.
    nudge.style.bottom = ((window.innerHeight - r.top) + 12) + "px";

    if (isFloating) {
      nudge.classList.remove("seymour-nudge--inline");
      nudge.classList.add("seymour-nudge--floating");
      // Tail's centre lands under the launcher's centre.
      const launcherCenter = r.left + r.width / 2;
      nudge.style.right = Math.max(8, window.innerWidth - launcherCenter - 32) + "px";
      nudge.style.left  = "";
    } else {
      nudge.classList.remove("seymour-nudge--floating");
      nudge.classList.add("seymour-nudge--inline");
      // Inline pill: avatar lives on the LEFT inside the pill. Read its
      // real position so the nudge tail lines up under it. Pick the
      // first VISIBLE candidate — querySelector returns display:none
      // elements too, and getBoundingClientRect on a hidden element is
      // all-zeros, which would slam the bubble against the viewport's
      // left edge (the bug Rick spotted).
      let hr = null;
      for (const sel of [".seymour-launcher-mascot", ".seymour-launcher-avatar"]) {
        const el = launcher.querySelector(sel);
        if (!el) continue;
        const rr = el.getBoundingClientRect();
        if (rr.width > 0 && rr.height > 0) { hr = rr; break; }
      }
      const avatarCenter = hr ? (hr.left + hr.width / 2) : (r.left + 20);
      // Tail's centre at avatar centre; tail sits 32px from bubble's
      // LEFT edge, so bubble's left edge = avatarCenter - 32.
      const bubbleLeft = Math.max(8, avatarCenter - 32);
      nudge.style.left  = bubbleLeft + "px";
      nudge.style.right = "";
    }
    nudge.style.top = "";
  }

  function pickPrompt() {
    if (NUDGE_PROMPTS.length <= 1) {
      _nudgeLastIdx = 0;
      return NUDGE_PROMPTS[0];
    }
    let idx;
    do { idx = Math.floor(Math.random() * NUDGE_PROMPTS.length); }
    while (idx === _nudgeLastIdx);
    _nudgeLastIdx = idx;
    return NUDGE_PROMPTS[idx];
  }

  function recentlyDismissed() {
    try {
      const t = parseInt(localStorage.getItem(NUDGE_DISMISS_KEY) || "0", 10);
      return t && (Date.now() - t < NUDGE_COOLDOWN_MS);
    } catch (_) { return false; }
  }

  function hideNudge() {
    nudge.classList.remove("open");
    _nudgeShown = false;
    if (_nudgeHideTimer) { clearTimeout(_nudgeHideTimer); _nudgeHideTimer = null; }
  }

  function scheduleNext() {
    if (_nudgeStopped) return;
    if (_nudgeNextTimer) clearTimeout(_nudgeNextTimer);
    _nudgeNextTimer = setTimeout(showNudge, NUDGE_GAP_MS);
  }

  function showNudge() {
    if (_nudgeStopped) return;
    if (_nudgeShown) return;
    if (recentlyDismissed()) { stopRotation(); return; }
    // Need the launcher to have been classified first (inline OR floating).
    const isInline   = launcher.classList.contains("seymour-launcher--inline");
    const isFloating = launcher.classList.contains("seymour-launcher--floating");
    if (!isInline && !isFloating) {
      // Launcher not placed yet — try again shortly.
      scheduleNext();
      return;
    }
    // Don't pop while the chat panel is already open.
    if (panel && panel.classList.contains("open")) {
      scheduleNext();
      return;
    }
    _nudgeShown = true;
    nudgeBody.innerHTML = pickPrompt();
    placeNudge();
    requestAnimationFrame(() => nudge.classList.add("open"));
    _nudgeHideTimer = setTimeout(() => {
      hideNudge();
      scheduleNext();    // wait → show → hide → wait → show … loop
    }, NUDGE_VISIBLE_MS);
  }

  function stopRotation() {
    _nudgeStopped = true;
    hideNudge();
    if (_nudgeNextTimer) { clearTimeout(_nudgeNextTimer); _nudgeNextTimer = null; }
  }

  function dismissNudge() {
    stopRotation();
    try { localStorage.setItem(NUDGE_DISMISS_KEY, String(Date.now())); } catch (_) {}
  }

  nudgeClose.addEventListener("click", (e) => {
    e.stopPropagation();
    dismissNudge();
  });
  // Clicking the nudge body itself opens the chat (any reasonable
  // intent to engage with it should bring up Seymour).
  nudge.addEventListener("click", (e) => {
    if (e.target === nudgeClose || nudgeClose.contains(e.target)) return;
    stopRotation();
    launcher.click();
  });
  // Any direct interaction with the launcher (open/close chat) stops
  // the rotation — customer has engaged, no need to keep poking.
  launcher.addEventListener("click", stopRotation);

  setTimeout(showNudge, NUDGE_INITIAL_DELAY_MS);

  const backdrop = document.createElement("div");
  backdrop.className = "seymour-backdrop";
  document.body.appendChild(backdrop);

  // eWAY card-payment modal scaffolding. Built once, reused; opened
  // when the bot emits a card_payment_modal event.
  const ewayBackdrop = document.createElement("div");
  ewayBackdrop.className = "seymour-eway-backdrop";
  document.body.appendChild(ewayBackdrop);
  const ewayModal = document.createElement("div");
  ewayModal.className = "seymour-eway-modal";
  ewayModal.innerHTML = `
    <div class="seymour-eway-head">
      <h3>Secure card payment</h3>
      <span class="seymour-eway-amount"></span>
      <button type="button" class="seymour-eway-close" aria-label="Cancel">×</button>
    </div>
    <div class="seymour-eway-body"></div>
    <div class="seymour-eway-foot">Card details are encrypted in your browser and sent direct to eWAY. StrataGreen does not see or store your card.</div>
  `;
  document.body.appendChild(ewayModal);
  const ewayBody  = ewayModal.querySelector(".seymour-eway-body");
  const ewayAmtEl = ewayModal.querySelector(".seymour-eway-amount");
  const ewayCloseBtn = ewayModal.querySelector(".seymour-eway-close");
  let ewayCurrentIntent = null;

  // Lazy-load eWAY's CSE library (eCrypt.min.js) the first time the
  // modal opens. Sandbox + prod use the same script — only the public
  // key differs, and that's per-merchant.
  let _eCryptLoading = null;
  function loadECrypt(envName) {
    if (window.eCrypt) return Promise.resolve();
    if (_eCryptLoading) return _eCryptLoading;
    // Sandbox host is secure-au.sandbox.ewaypayments.com (with -au).
    // The unprefixed secure.sandbox... hostname doesn't resolve.
    const host = envName === "prod"
      ? "https://secure.ewaypayments.com"
      : "https://secure-au.sandbox.ewaypayments.com";
    _eCryptLoading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = host + "/scripts/eCrypt.min.js";
      s.onload  = () => resolve();
      s.onerror = () => reject(new Error("eCrypt load failed"));
      document.head.appendChild(s);
    });
    return _eCryptLoading;
  }

  function openEwayModal(modalData) {
    ewayCurrentIntent = modalData;
    ewayAmtEl.textContent = "$" + Number(modalData.amount || 0).toFixed(2) + " AUD";
    ewayBody.innerHTML = renderEwayForm(modalData);
    ewayBackdrop.classList.add("open");
    ewayModal.classList.add("open");
    wireEwayForm(modalData);
    // Kick off the eCrypt load if we haven't already. By the time the
    // customer finishes typing card details it's almost always cached.
    loadECrypt(modalData.eway_env || "sandbox").catch(err =>
      console.error("[seymour] eCrypt failed to load", err));
  }

  // Preload eCrypt opportunistically — the chatbot is on every storefront
  // page, but card payments are rare, so we don't want to block init on
  // this. Idle-callback + setTimeout fallback for browsers that don't
  // support it. ~30KB across the wire.
  (window.requestIdleCallback || function (fn) { setTimeout(fn, 2000); })(() => {
    loadECrypt("sandbox").catch(() => { /* will retry on modal open */ });
  });

  function renderEwayForm(d) {
    const total = Number(d.amount || 0).toFixed(2);
    const defaultName = (d.card_holder_default || "").replace(/"/g, "&quot;");
    return (
      '<form class="seymour-eway-form" novalidate>' +
        '<label class="seymour-eway-field">' +
          '<span>Name on card</span>' +
          '<input type="text" name="cardname" autocomplete="cc-name" required value="' + defaultName + '">' +
        '</label>' +
        '<label class="seymour-eway-field">' +
          '<span>Card number</span>' +
          '<input type="text" name="cardnumber" inputmode="numeric" autocomplete="cc-number" required maxlength="23" placeholder="•••• •••• •••• ••••">' +
        '</label>' +
        '<div class="seymour-eway-row">' +
          '<label class="seymour-eway-field seymour-eway-mm">' +
            '<span>Month</span>' +
            '<input type="text" name="mm" inputmode="numeric" autocomplete="cc-exp-month" required maxlength="2" placeholder="MM">' +
          '</label>' +
          '<label class="seymour-eway-field seymour-eway-yy">' +
            '<span>Year</span>' +
            '<input type="text" name="yy" inputmode="numeric" autocomplete="cc-exp-year" required maxlength="4" placeholder="YY">' +
          '</label>' +
          '<label class="seymour-eway-field seymour-eway-cvn">' +
            '<span>CVN</span>' +
            '<input type="text" name="cvn" inputmode="numeric" autocomplete="cc-csc" required maxlength="4" placeholder="123">' +
          '</label>' +
        '</div>' +
        '<div class="seymour-eway-error" role="alert"></div>' +
        '<button type="submit" class="seymour-eway-pay">Pay $' + total + ' AUD</button>' +
        // Trust badge — inline SVG lock + "Secured by eWAY" with a
        // link out to eWAY for customers who want to verify.
        '<div class="seymour-eway-trust">' +
          '<svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
            '<path d="M3 6V4a3 3 0 1 1 6 0v2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>' +
            '<rect x="1.5" y="6" width="9" height="7" rx="1.4" stroke="currentColor" stroke-width="1.2"/>' +
            '<circle cx="6" cy="9.5" r="0.9" fill="currentColor"/>' +
          '</svg>' +
          '<span>Card details encrypted in your browser · ' +
          '<a href="https://www.eway.com.au" target="_blank" rel="noopener">Secured by <strong>eWAY</strong></a></span>' +
        '</div>' +
      '</form>'
    );
  }

  function wireEwayForm(modalData) {
    const form    = ewayBody.querySelector(".seymour-eway-form");
    const btn     = form.querySelector(".seymour-eway-pay");
    const errEl   = form.querySelector(".seymour-eway-error");
    const ccNumEl = form.elements.cardnumber;
    // Light affordance — space every 4 digits as the user types.
    ccNumEl.addEventListener("input", () => {
      const digits = ccNumEl.value.replace(/\D/g, "").slice(0, 19);
      ccNumEl.value = digits.replace(/(.{4})/g, "$1 ").trim();
    });

    form.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      errEl.textContent = "";
      // Wait for eCrypt to load if it isn't ready yet — typical first-open
      // hits this because the modal can render before the 30KB script
      // arrives. After this, eCrypt is cached.
      if (!window.eCrypt) {
        errEl.textContent = "Connecting to secure payment service…";
        try {
          await loadECrypt(modalData.eway_env || "sandbox");
        } catch (e) {
          errEl.textContent = "Couldn't load the secure payment library — refresh the page and try again.";
          return;
        }
        errEl.textContent = "";
      }
      const name   = form.elements.cardname.value.trim();
      const number = form.elements.cardnumber.value.replace(/\D/g, "");
      const cvn    = form.elements.cvn.value.trim();
      const mm     = form.elements.mm.value.trim().padStart(2, "0");
      const yy     = form.elements.yy.value.trim();
      if (!name || !number || !cvn || !mm || !yy) {
        errEl.textContent = "Please complete all fields.";
        return;
      }
      btn.disabled = true;
      const origLabel = btn.textContent;
      btn.textContent = "Processing…";
      let encryptedNumber, encryptedCvn;
      try {
        encryptedNumber = window.eCrypt.encryptValue(number, modalData.eway_public_key);
        encryptedCvn    = window.eCrypt.encryptValue(cvn,    modalData.eway_public_key);
      } catch (e) {
        console.error("[seymour] eCrypt encrypt failed", e);
        errEl.textContent = "Couldn't encrypt the card details — please try again.";
        btn.disabled = false; btn.textContent = origLabel;
        return;
      }
      try {
        const r = await fetch(API_BASE + "/api/chat/charge", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            intent_token:          modalData.intent_token,
            card_holder_name:      name,
            encrypted_card_number: encryptedNumber,
            encrypted_card_cvn:    encryptedCvn,
            expiry_month:          mm,
            expiry_year:           yy.length === 2 ? "20" + yy : yy,
          }),
        });
        const out = await r.json();
        if (out.ok) {
          closeEwayModal();
          const lines = [
            "Thank you for your order — much appreciated. 🌱",
            "",
            "Your order reference is **" + (out.order_nbr || "(pending)") + "**.",
            "Payment received, eWAY reference **" + (out.eway_txid || "") + "**" +
              (out.auth_code ? " (auth " + out.auth_code + ")" : "") + ".",
            "",
            "Sales will be in touch shortly to confirm dispatch.",
          ];
          const msg = lines.join("\n");
          appendMsg(msg, "seymour-bot");
          recordEntry({ kind: "bot", text: msg });
        } else {
          errEl.textContent = out.message || "Payment didn't go through.";
          btn.disabled = false; btn.textContent = origLabel;
        }
      } catch (e) {
        console.error("[seymour] /api/chat/charge failed", e);
        errEl.textContent = "Couldn't reach our server — please try again.";
        btn.disabled = false; btn.textContent = origLabel;
      }
    });
  }
  function closeEwayModal() {
    ewayBackdrop.classList.remove("open");
    ewayModal.classList.remove("open");
    ewayBody.innerHTML = "";
    ewayCurrentIntent = null;
  }
  ewayCloseBtn.addEventListener("click", () => {
    if (ewayCurrentIntent) {
      appendMsg("Payment cancelled. Let me know if you want to try again or go a different route.", "seymour-bot");
      recordEntry({ kind: "bot", text: "Payment cancelled. Let me know if you want to try again or go a different route." });
    }
    closeEwayModal();
  });
  // ewayBackdrop click does NOT close — we don't want a stray tap to
  // kill a half-entered card form. Customer must use the × button or
  // complete the payment.

  // CSE flow has no iframe and no postMessage handshake — the form
  // submit handler calls /api/chat/charge directly. Result rendering
  // happens inline in wireEwayForm().

  const panel = document.createElement("div");
  panel.className = "seymour-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", "Chat with Seymour");
  panel.innerHTML = `
    <div class="seymour-header">
      ${mascotUrl ? `<img class="seymour-mascot" src="${mascotUrl}" alt="" aria-hidden="true">` : ""}
      <div class="seymour-avatar" ${avatarUrl ? `style="background-image:url('${avatarUrl}')"` : ""}></div>
      <div class="seymour-header-text">
        <h2>Seymour</h2>
        <div class="seymour-sub">Your AI Landscape &amp; Horticulture Assistant</div>
      </div>
      <button type="button" class="seymour-close" aria-label="Close">×</button>
    </div>
    <div class="seymour-greeting-bubble" role="status"></div>
    <div class="seymour-chat" aria-live="polite"></div>
    <form class="seymour-form" autocomplete="off">
      <input type="text" placeholder="Ask about a product, stock, or your order…" required>
      <button type="submit">Send</button>
    </form>
    <div class="seymour-disclaimer">
      Beta — responses may be wrong. For anything important, email
      <a href="mailto:sales@stratagreen.com.au">sales@stratagreen.com.au</a>.
    </div>
  `;
  document.body.appendChild(panel);

  const chatEl = panel.querySelector(".seymour-chat");
  const formEl = panel.querySelector(".seymour-form");
  const inputEl = formEl.querySelector("input");
  const sendBtn = formEl.querySelector("button");
  const closeBtn = panel.querySelector(".seymour-close");

  // ------------------- embed mode -------------------
  // If the page provides a <div id="seymour-chat-embed">, mount the
  // chat panel INSIDE it (inline, container-sized) instead of as a
  // floating popup. The floating launcher + backdrop disappear and the
  // panel is always-open. Used on the /pages/ask-seymour landing page.
  //
  // The embed container can carry data-min-width="700" (default 700)
  // — below that viewport width, embedded mode is suppressed and the
  // normal floating launcher takes over (better mobile experience
  // than cramming the embedded chat into a narrow column).
  let _embedActive = false;
  let _embedHomeParent = panel.parentNode;       // body, the panel's original home
  let _embedGreetedHere = false;
  let _embedAutoOpenedNarrow = false;            // ensures we auto-open ONCE per page load
  function findEmbedContainer() {
    return document.getElementById("seymour-chat-embed");
  }
  function applyEmbed() {
    const container = findEmbedContainer();
    if (!container) return;
    const minWidth = parseInt(container.getAttribute("data-min-width") || "700", 10);
    const shouldEmbed = window.innerWidth >= minWidth;
    if (shouldEmbed && !_embedActive) {
      // Move panel into the container, switch styling to embedded mode,
      // hide the launcher + backdrop, force-open the panel.
      container.appendChild(panel);
      panel.classList.add("seymour-panel--embedded", "open");
      backdrop.classList.remove("open");
      launcher.style.display = "none";
      // Greet once on first mount if the chat is empty.
      if (!_embedGreetedHere && !chatEl.children.length) {
        _embedGreetedHere = true;
        if (history.length) {
          replayHistory();
          if (!chatEl.children.length) greetInChat();
        } else {
          greetInChat();
        }
      }
      _embedActive = true;
    } else if (!shouldEmbed && _embedActive) {
      // Width dropped below the embed threshold mid-session — pop the
      // panel back out of the embed container and let it carry on as a
      // floating modal. Keep `open` so the customer's conversation
      // continues seamlessly (no jarring close).
      _embedHomeParent.appendChild(panel);
      panel.classList.remove("seymour-panel--embedded");
      backdrop.classList.add("open");
      launcher.style.display = "";
      _embedActive = false;
    }

    // On narrow viewports where embed mode is suppressed, auto-open
    // the floating panel ONCE per page load — the customer arrived on
    // a chat-focused landing page (the embed container is the signal),
    // so dropping them straight into a chat is the right UX. Set the
    // guard flag whenever we're in the narrow state so close()ing the
    // panel doesn't get undone on a subsequent resize.
    if (!shouldEmbed && !_embedAutoOpenedNarrow) {
      _embedAutoOpenedNarrow = true;
      if (!panel.classList.contains("open")) {
        try { open(); } catch (e) { console.error("[seymour] landing-page auto-open failed:", e); }
      }
    }
  }
  function greetInChat() {
    // Embed-mode greeting: a normal chat bubble rather than the
    // floating speech bubble (which is positioned relative to the
    // floating panel and doesn't make sense embedded).
    const name = (CFG.customerName || "").trim();
    const hello = name ? "Hi " + name + "!" : "Hi there!";
    const text  = hello +
      " I'm Seymour, StrataGreen's AI Landscape & Horticulture Assistant. " +
      "I can help you find products, check stock, pull invoices, " +
      "or just chat about your project. What can I help with today?";
    appendMsg(text, "seymour-bot");
    if (CFG.customerId && CFG.authToken && CFG.authTs) checkAbandonedCart();
  }

  // ------------------- launcher behaviour -------------------
  function open() {
    panel.classList.add("open");
    backdrop.classList.add("open");
    localStorage.setItem(OPEN_KEY, "1");
    setTimeout(() => inputEl.focus(), 100);
    if (!chatEl.children.length) {
      if (history.length) {
        replayHistory();
        if (!chatEl.children.length) {
          // Replay produced nothing visible (every entry threw, or the
          // history is corrupt). Fall back to the greeting so the panel
          // isn't blank.
          console.warn("[seymour] replay produced 0 DOM children; falling back to greet");
          greet();
        }
      } else {
        greet();
      }
    }
  }

  function replayHistory() {
    console.log("[seymour] replayHistory: " + history.length + " entries",
                history.map(function (e) { return e && e.kind; }));
    for (let i = 0; i < history.length; i++) {
      const entry = history[i];
      if (!entry || !entry.kind) continue;
      try {
        switch (entry.kind) {
          case "user":
            appendMsg(entry.text || "", "seymour-user");
            break;
          case "bot":
            appendMsg(entry.text || "", "seymour-bot");
            break;
          case "err": {
            const d = appendMsg(entry.text || "", "seymour-bot");
            d.classList.add("seymour-err");
            break;
          }
          case "cards":
            appendProductCards(entry.products || [], { label: entry.label });
            break;
          case "content":
            appendContentCards(entry.items || []);
            break;
          case "cart_confirm":
            appendCartConfirm(entry.data || {});
            break;
          case "download":
            appendDownloadLink({
              url: entry.url, label: entry.label,
              expires_in_seconds: entry.expires_in_seconds,
            });
            break;
          case "delivery_choice":
            // Replayed buttons go in as inert — they belonged to a previous
            // turn and the choice has long since happened.
            appendDeliveryChoice(entry.data || {}, { disabled: true });
            break;
          case "abandoned_cart_tile":
            appendAbandonedCartTile(entry.data || {});
            break;
          case "shipment_tile":
            appendShipmentTile(entry.data || {});
            break;
        }
      } catch (err) {
        // One bad entry shouldn't kill the rest of the replay.
        console.error("[seymour] replay entry " + i + " (" + entry.kind + ") failed:", err);
      }
    }
    // On reopen, jump to the bottom so the user sees the most recent
    // state of the previous conversation, not the start.
    chatEl.scrollTop = chatEl.scrollHeight;
  }
  function close() {
    panel.classList.remove("open");
    backdrop.classList.remove("open");
    localStorage.removeItem(OPEN_KEY);
    resetKeyboardPin();
  }
  launcher.addEventListener("click", () => (panel.classList.contains("open") ? close() : open()));

  // ------------------- mobile keyboard handling -------------------
  // Pin the panel's bottom edge to just above the soft keyboard when
  // it opens. CSS `100dvh` shrinks the viewport correctly, but the
  // fullscreen panel still ends up centred behind the keyboard because
  // mobile browsers don't always relayout fixed-position elements
  // against the visual viewport. The VisualViewport API tells us
  // exactly how much of the screen is visible — apply that as explicit
  // panel.height + top so the form sits flush above the keyboard.
  //
  // Desktop and embedded modes ignore this entirely.
  let _keyboardPinRAF = null;
  function adjustForKeyboard() {
    if (!window.visualViewport) return;
    if (panel.classList.contains("seymour-panel--embedded")) return;
    if (!panel.classList.contains("open")) return;
    const vv = window.visualViewport;
    // Detect keyboard (or other viewport-shrinking overlay) by
    // comparing the visual viewport to the layout viewport. A buffer
    // accounts for browser chrome auto-hiding.
    const keyboardOpen = (window.innerHeight - vv.height) > 100;
    if (!keyboardOpen) {
      resetKeyboardPin();
      return;
    }
    if (_keyboardPinRAF) return;
    _keyboardPinRAF = requestAnimationFrame(() => {
      _keyboardPinRAF = null;
      panel.style.height = vv.height + "px";
      panel.style.top    = vv.offsetTop + "px";
      panel.style.bottom = "auto";
      panel.style.transform = "none";   // override any centring transform
    });
  }
  function resetKeyboardPin() {
    panel.style.height = "";
    panel.style.top    = "";
    panel.style.bottom = "";
    panel.style.transform = "";
  }
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", adjustForKeyboard);
    window.visualViewport.addEventListener("scroll", adjustForKeyboard);
  }
  // Belt-and-braces — some Android browsers don't fire visualViewport
  // events reliably; refire on input focus too. Brief 100ms delay so
  // the keyboard has actually animated up by the time we measure.
  inputEl.addEventListener("focus", () => {
    setTimeout(adjustForKeyboard, 100);
    setTimeout(adjustForKeyboard, 350);
  });
  inputEl.addEventListener("blur", () => {
    setTimeout(adjustForKeyboard, 100);
  });
  closeBtn.addEventListener("click", close);
  backdrop.addEventListener("click", close);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && panel.classList.contains("open")) close();
  });

  if (localStorage.getItem(OPEN_KEY)) {
    // CRITICAL: must not let auto-open throw. If it did (e.g. a bug in
    // replayHistory), the IIFE would unwind before the form submit
    // listener gets attached, leaving the input bound to the browser's
    // default form POST — which manifests as "Send refreshes the page".
    try { open(); } catch (err) {
      console.error("[seymour] auto-open threw; continuing init:", err);
    }
  }

  // Embed mode wiring — mount the panel into #seymour-chat-embed if
  // present and viewport is wide enough. Re-evaluated on resize so
  // rotating a tablet (or resizing a desktop window) switches between
  // embedded and floating cleanly. Tied into the same resize listener
  // that handles launcher placement.
  try { applyEmbed(); } catch (err) { console.error("[seymour] applyEmbed threw:", err); }
  window.addEventListener("resize", applyEmbed);
  // The page's embed container might not exist at IIFE-run time (some
  // themes inject section markup after the layout script runs). Retry
  // on load + a short timer just in case.
  window.addEventListener("load", applyEmbed);
  setTimeout(applyEmbed, 250);

  function greet() {
    const name = (CFG.customerName || "").trim();
    const hello = name ? "Hi " + name + "!" : "Hi there!";
    // Render the greeting in the floating speech-bubble (above the
    // panel, tail pointing down at Seymour's mouth). Keep it to 3
    // lines — CSS clamps anything longer.
    const greetEl = panel.querySelector(".seymour-greeting-bubble");
    if (greetEl) {
      greetEl.innerHTML =
        '<strong>' + hello + '</strong> ' +
        "I'm Seymour, StrataGreen's AI Landscape &amp; Horticulture Assistant. " +
        "What can I help with today?";
      requestAnimationFrame(() => greetEl.classList.add("open"));
    }

    // Proactive abandoned-cart probe — only for verified customers, only
    // when a recent (≤14d) open checkout exists. Fire-and-forget; failure
    // shouldn't disturb the greeting.
    if (CFG.customerId && CFG.authToken && CFG.authTs) {
      checkAbandonedCart();
    }
  }

  async function checkAbandonedCart() {
    try {
      const body = {
        session_id:            sessionId,
        customer_id:           CFG.customerId,
        customer_email:        CFG.customerEmail,
        company_id:            CFG.companyId           || "",
        company_location_id:   CFG.companyLocationId   || "",
        acumatica_customer_id: CFG.acumaticaCustomerId || "",
        auth_token:            CFG.authToken,
        auth_ts:               CFG.authTs,
      };
      const r = await fetch(API_BASE + "/api/abandoned-cart-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) return;
      const data = await r.json();
      if (!data.has_cart || !data.cart) return;
      const c = data.cart;
      const ageStr =
        c.age_days != null && c.age_days < 1
          ? "earlier today"
          : (Math.round(c.age_days) <= 1
              ? "yesterday"
              : Math.round(c.age_days) + " days ago");
      const summary =
        "Welcome back — looks like you left some items in your cart " + ageStr + ". Want to pick up where you left off?";
      appendMsg(summary, "seymour-bot");
      recordEntry({ kind: "bot", text: summary });
      appendAbandonedCartTile(c);
      recordEntry({ kind: "abandoned_cart_tile", data: c });
    } catch (_) { /* swallow — proactive only */ }
  }

  // Wipe any beta-era access tokens that might still be in localStorage —
  // the bot no longer uses one. Harmless if absent.
  try { localStorage.removeItem(TOKEN_KEY); } catch (_) {}

  // ------------------- DOM helpers -------------------

  // Markdown rendering. `marked` is loaded as a vendored script via the
  // theme snippet. Configure once with safe defaults: GFM tables/lists,
  // line-breaks=true (so single newlines render as <br>), and we don't
  // enable raw-HTML pass-through so any HTML in the source is escaped.
  let _markedReady = false;
  function ensureMarked() {
    if (_markedReady || typeof window.marked === "undefined") return _markedReady;
    window.marked.setOptions({ gfm: true, breaks: true });
    _markedReady = true;
    return true;
  }
  function renderMarkdown(text) {
    if (!ensureMarked()) {
      // Marked not loaded — fall back to plain text, preserving newlines.
      const span = document.createElement("span");
      span.style.whiteSpace = "pre-wrap";
      span.textContent = text;
      return span.outerHTML;
    }
    // marked.parse returns string; the input is markdown so HTML in it
    // is already escaped by marked's default behaviour. Force all links
    // open in new tabs so clicks don't navigate away from the storefront.
    const html = window.marked.parse(text || "");
    return html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
  }

  function appendMsg(text, cls) {
    const div = document.createElement("div");
    div.className = "seymour-msg " + cls;
    if (cls.indexOf("seymour-bot") >= 0) {
      div.innerHTML = renderMarkdown(text);
    } else {
      div.textContent = text;   // user + error bubbles stay plain
    }
    chatEl.appendChild(div);
    if (cls.indexOf("seymour-user") >= 0) {
      // Pin the user's question at the top of the visible area so the
      // bot's reply streams in below, and the user reads top-down
      // without having to scroll back up to find their question.
      // Pad chatEl so there's room to position the user msg at the top
      // even when there's little content below it yet.
      chatEl.style.paddingBottom = chatEl.clientHeight + "px";
      // Use getBoundingClientRect (not offsetTop — that's relative to
      // the nearest *positioned* ancestor, which may not be chatEl, so
      // it can scroll past the question and hide it).
      const userTop = div.getBoundingClientRect().top
                    - chatEl.getBoundingClientRect().top
                    + chatEl.scrollTop;
      chatEl.scrollTop = Math.max(0, userTop - 6);
    }
    // For bot/err messages: don't auto-scroll — the anchor set on the
    // most recent user message stays in place.
    return div;
  }
  function appendTool(name) {
    const t = document.createElement("div");
    t.className = "seymour-tool";
    t.textContent = "↳ checking " + name.replace(/_/g, " ") + "…";
    chatEl.appendChild(t);
  }

  function formatPriceRange(pr) {
    if (!pr) return "";
    const min = pr.min, max = pr.max;
    if (min == null && max == null) return "";
    const fmt = (n) => "$" + Number(n).toFixed(2);
    if (min != null && max != null && min !== max) return fmt(min) + "–" + fmt(max);
    return fmt(min != null ? min : max);
  }

  function appendContentCards(items) {
    if (!items || !items.length) return;
    const rail = document.createElement("div");
    rail.className = "seymour-cards";
    for (const a of items) {
      if (!a.online_store_url) continue;
      const link = document.createElement("a");
      link.className = "seymour-card seymour-card-content";
      link.href = a.online_store_url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";

      if (a.image_url) {
        const img = document.createElement("img");
        img.src = a.image_url;
        img.alt = a.title || "";
        img.loading = "lazy";
        link.appendChild(img);
      } else {
        const ph = document.createElement("div");
        ph.className = "seymour-card-noimg";
        ph.textContent = a.type === "page" ? "Page" : "Article";
        link.appendChild(ph);
      }
      const body = document.createElement("div");
      body.className = "seymour-card-body";

      const badge = document.createElement("div");
      badge.className = "seymour-card-meta seymour-content-badge";
      badge.textContent = a.type === "page" ? "Page" :
        ("Blog" + (a.blog ? " · " + a.blog.replace(/-/g, " ") : ""));
      body.appendChild(badge);

      const title = document.createElement("div");
      title.className = "seymour-card-title";
      title.textContent = a.title || "";
      body.appendChild(title);

      if (a.summary) {
        const sum = document.createElement("div");
        sum.className = "seymour-card-summary";
        sum.textContent = a.summary;
        body.appendChild(sum);
      }
      link.appendChild(body);
      rail.appendChild(link);
    }
    if (rail.childElementCount > 0) {
      chatEl.appendChild(rail);
    }
  }

  function appendProductCards(products, opts) {
    if (!products || !products.length) return;
    opts = opts || {};
    if (opts.label) {
      // Optional "Recommended products" heading for sub-rails that come
      // from article.feature_products etc. Plain rails skip this.
      const head = document.createElement("div");
      head.className = "seymour-cards-label";
      head.textContent = opts.label;
      chatEl.appendChild(head);
    }
    const rail = document.createElement("div");
    rail.className = "seymour-cards";
    for (const p of products) {
      // Acumatica-only items have no Shopify listing (no online_store_url,
      // no cart link) — render them as info-only tiles instead of dropping.
      // Shopify tiles keep their <a> shape so the whole card is clickable.
      const isAcu = (p.source === "acumatica");
      if (!isAcu && !p.online_store_url) continue;

      const card = document.createElement(isAcu ? "div" : "a");
      card.className = "seymour-card" + (isAcu ? " seymour-card-acu" : "");
      if (!isAcu) {
        card.href = p.online_store_url;
        card.target = "_blank";
        card.rel = "noopener noreferrer";
      }
      if (p.image_url) {
        const img = document.createElement("img");
        img.src = p.image_url;
        img.alt = p.title || "";
        img.loading = "lazy";
        card.appendChild(img);
      } else {
        const ph = document.createElement("div");
        ph.className = "seymour-card-noimg";
        ph.textContent = "StrataGreen";
        card.appendChild(ph);
      }
      const body = document.createElement("div");
      body.className = "seymour-card-body";
      const title = document.createElement("div");
      title.className = "seymour-card-title";
      title.textContent = p.title || "";
      body.appendChild(title);

      // Authenticated trade customers get "your_price_range" populated;
      // show that as the primary price (matches what they actually pay
      // after their trade discount). Otherwise fall back to the
      // Shopify-displayed retail range. tax_label reflects what's
      // included (GST, GST + Nursery Levy, GST-free, etc.) per the
      // product's Acumatica TaxCategory.
      const yourPrice = formatPriceRange(p.your_price_range);
      const retailPrice = formatPriceRange(p.price_range);
      const taxLabel = p.tax_label || "inc GST";
      if (yourPrice) {
        const meta = document.createElement("div");
        meta.className = "seymour-card-meta seymour-your-price";
        meta.textContent = "your price " + yourPrice + " " + taxLabel;
        body.appendChild(meta);
        if (p.variant_count > 1) {
          const sub = document.createElement("div");
          sub.className = "seymour-card-meta";
          sub.textContent = p.variant_count + " options";
          body.appendChild(sub);
        }
      } else if (retailPrice || p.variant_count > 1) {
        const meta = document.createElement("div");
        meta.className = "seymour-card-meta";
        meta.textContent = [
          retailPrice && (retailPrice + " " + taxLabel),
          p.variant_count > 1 && (p.variant_count + " options"),
        ].filter(Boolean).join(" · ");
        body.appendChild(meta);
      }
      // Stock chip — only renders for decision-relevant states (low / out /
      // out-with-ETA). p.stock_status is null for healthy stock, keeping
      // the card uncluttered for the common case.
      if (p.stock_status && p.stock_status.label) {
        const chip = document.createElement("span");
        chip.className = "seymour-stock-chip seymour-stock-" + (p.stock_status.tone || "warn");
        chip.textContent = p.stock_status.label;
        body.appendChild(chip);
      }
      // Acumatica-only tiles get an explicit "no online listing" note so
      // the tile doesn't look broken (no clickable image, no cart button).
      if (isAcu) {
        const note = document.createElement("div");
        note.className = "seymour-card-meta seymour-card-acu-note";
        note.textContent = "In our catalogue — ask about a quote";
        body.appendChild(note);
      }
      card.appendChild(body);
      rail.appendChild(card);
    }
    chatEl.appendChild(rail);
  }

  // ------------------- cart integration -------------------
  function appendCartConfirm({ ok, productTitle, variantTitle, imageUrl, quantity, error }) {
    const tile = document.createElement("div");
    tile.className = "seymour-cart-confirm" + (ok ? "" : " seymour-err");

    if (imageUrl) {
      const img = document.createElement("img");
      img.src = imageUrl;
      img.alt = productTitle || "";
      tile.appendChild(img);
    }

    const text = document.createElement("div");
    text.className = "seymour-cart-confirm-text";
    if (ok) {
      text.innerHTML =
        '<strong>Added to your cart ✓</strong>' +
        '<div class="seymour-vt"></div>';
      text.querySelector("strong").textContent =
        `${quantity}× ${productTitle || "item"} — added to your cart`;
      text.querySelector(".seymour-vt").textContent = variantTitle || "";
    } else {
      text.innerHTML = "<strong>Couldn't add to cart</strong>";
      const sub = document.createElement("div");
      sub.className = "seymour-vt";
      sub.textContent = error || "";
      text.appendChild(sub);
    }
    tile.appendChild(text);

    if (ok && SHOPIFY_NATIVE) {
      const cta = document.createElement("a");
      cta.className = "seymour-cart-confirm-cta";
      cta.href = "/cart";
      cta.textContent = "View cart";
      tile.appendChild(cta);
    }

    chatEl.appendChild(tile);
  }

  // Pure render — must NOT record to history (replay calls it; if it
  // recorded, every page load would double the saved entries).
  // Abandoned-cart recovery tile — same visual family as the
  // download-link tile but with item count + total + a "Resume checkout"
  // button that opens the Shopify recovery URL in a new tab. Customers
  // who click land back at exactly the step they bailed at.
  function appendAbandonedCartTile(c) {
    const tile = document.createElement("div");
    tile.className = "seymour-cart-confirm";
    const text = document.createElement("div");
    text.className = "seymour-cart-confirm-text";
    text.innerHTML = '<strong></strong><div class="seymour-vt"></div>';
    const itemCount = c.item_count || 0;
    const total = c.total_amount != null ? (" — $" + Number(c.total_amount).toFixed(2)) : "";
    text.querySelector("strong").textContent =
      itemCount + (itemCount === 1 ? " item" : " items") + " in your cart" + total;
    const ageStr = c.age_days != null && c.age_days < 1
      ? "started earlier today"
      : (Math.round(c.age_days) <= 1
          ? "started yesterday"
          : "started " + Math.round(c.age_days) + " days ago");
    const itemPreview = (c.items || []).slice(0, 2).map(function (it) {
      return it.quantity + " × " + (it.title || it.sku || "");
    }).join(", ");
    text.querySelector(".seymour-vt").textContent =
      ageStr + (itemPreview ? " — " + itemPreview : "");
    tile.appendChild(text);
    const cta = document.createElement("a");
    cta.className = "seymour-cart-confirm-cta";
    cta.href = c.recovery_url;
    cta.target = "_blank";
    cta.rel = "noopener noreferrer";
    cta.textContent = "Resume checkout";
    tile.appendChild(cta);
    chatEl.appendChild(tile);
  }

  // Shipment-tracking tile — same visual family as the abandoned-cart
  // / download-link tile. Shows shipment number + plain-English status
  // + a "Track shipment" button linking to MachShip's hosted page.
  function appendShipmentTile(ev) {
    const tile = document.createElement("div");
    tile.className = "seymour-cart-confirm";
    const text = document.createElement("div");
    text.className = "seymour-cart-confirm-text";
    text.innerHTML = '<strong></strong><div class="seymour-vt"></div>';
    const head = "Shipment " + (ev.shipment_nbr || ev.consignment_number || "(pending)");
    text.querySelector("strong").textContent = head;
    const bits = [];
    if (ev.status_plain) bits.push(ev.status_plain);
    if (ev.total_item_count) {
      bits.push(ev.total_item_count + (ev.total_item_count === 1 ? " package" : " packages"));
    }
    if (ev.total_weight_kg) bits.push(Math.round(ev.total_weight_kg) + " kg");
    text.querySelector(".seymour-vt").textContent = bits.join(" · ");
    tile.appendChild(text);
    if (ev.tracking_url) {
      const cta = document.createElement("a");
      cta.className = "seymour-cart-confirm-cta";
      cta.href = ev.tracking_url;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.textContent = "Track shipment";
      tile.appendChild(cta);
    }
    chatEl.appendChild(tile);
  }

  function appendDownloadLink(ev) {
    const tile = document.createElement("div");
    tile.className = "seymour-cart-confirm";  // reuse the same tile look
    const text = document.createElement("div");
    text.className = "seymour-cart-confirm-text";
    text.innerHTML = '<strong></strong><div class="seymour-vt"></div>';
    text.querySelector("strong").textContent = ev.label || "Download";
    // Public product-document URLs come in with expires_in_seconds=0 — show
    // a simpler sub-line in that case. Signed invoice/statement URLs pass
    // a positive expiry and keep the "valid for N minutes" wording.
    const subEl = text.querySelector(".seymour-vt");
    if (ev.expires_in_seconds && ev.expires_in_seconds > 0) {
      const mins = Math.max(1, Math.round(ev.expires_in_seconds / 60));
      subEl.textContent = "Link valid for " + mins + " minutes — opens in a new tab";
    } else {
      subEl.textContent = "PDF — opens in a new tab";
    }
    tile.appendChild(text);

    const cta = document.createElement("a");
    cta.className = "seymour-cart-confirm-cta";
    cta.href = ev.url;
    cta.target = "_blank";
    cta.rel = "noopener noreferrer";
    cta.textContent = "Open PDF";
    tile.appendChild(cta);

    chatEl.appendChild(tile);
  }

  // Render the inline two-button "email or download?" picker the bot
  // emits via offer_delivery_choice. Click on a button (a) disables both
  // (b) drops the natural-language reply into the input box and submits
  // the form, so the bot processes it as a normal turn.
  function appendDeliveryChoice(data, opts) {
    opts = opts || {};
    const tile = document.createElement("div");
    tile.className = "seymour-delivery-choice";

    const head = document.createElement("div");
    head.className = "seymour-delivery-head";
    head.textContent = "How would you like " +
      (data.label || "it") + "?";
    tile.appendChild(head);

    const row = document.createElement("div");
    row.className = "seymour-delivery-buttons";

    function disableAll() {
      tile.querySelectorAll("button").forEach((b) => {
        b.disabled = true;
        b.classList.add("seymour-chosen-out");
      });
    }

    function makeBtn(label, replyText, primary) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "seymour-delivery-btn" + (primary ? " seymour-delivery-primary" : "");
      b.textContent = label;
      if (opts.disabled) {
        b.disabled = true;
      } else {
        b.addEventListener("click", () => {
          disableAll();
          b.classList.add("seymour-chosen");
          // Drop the synthesised reply into the input and submit so the
          // turn flows through the same path as a typed message — appears
          // as a user bubble in chat, gets logged, hits the bot tools.
          inputEl.value = replyText;
          formEl.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        });
      }
      return b;
    }

    const id = data.doc_id || "";
    let emailReply, downloadReply;
    if (data.doc_type === "invoice") {
      emailReply    = "Email me invoice " + id;
      downloadReply = "Give me a download link for invoice " + id;
    } else {
      emailReply    = "Email me my statement";
      downloadReply = "Give me a download link for my statement";
    }
    row.appendChild(makeBtn("📧 Email me", emailReply, true));
    row.appendChild(makeBtn("📥 Download here", downloadReply, false));
    tile.appendChild(row);

    chatEl.appendChild(tile);
  }

  async function handleCartAdd(ev) {
    const persist = (data) => {
      appendCartConfirm(data);
      recordEntry({ kind: "cart_confirm", data });
    };
    if (!SHOPIFY_NATIVE) {
      persist({
        ok: false,
        productTitle: ev.product_title,
        variantTitle: ev.variant_title,
        imageUrl: ev.image_url,
        quantity: ev.quantity,
        error: "Cart only works inside the StrataGreen storefront. Open " +
               (ev.online_store_url || "the product page") + " to buy.",
      });
      return;
    }
    if (!ev.variant_id) {
      persist({
        ok: false, productTitle: ev.product_title,
        error: "Missing variant ID — try the product page.",
      });
      return;
    }
    try {
      const resp = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: ev.variant_id, quantity: ev.quantity || 1 }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        persist({
          ok: false, productTitle: ev.product_title,
          error: body.description || ("Shopify rejected the add (HTTP " + resp.status + ")"),
        });
        return;
      }
      persist({
        ok: true,
        productTitle: ev.product_title,
        variantTitle: ev.variant_title,
        imageUrl: ev.image_url,
        quantity: ev.quantity || 1,
      });
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    } catch (err) {
      persist({
        ok: false, productTitle: ev.product_title,
        error: err.message,
      });
    }
  }

  // ------------------- health check -------------------
  // Done lazily on first open — avoids noise on every page load.
  let healthChecked = false;
  function checkHealth() {
    if (healthChecked) return;
    healthChecked = true;
    fetch(API_BASE + "/healthz").catch((err) => {
      console.warn("[seymour] backend offline:", err);
    });
  }

  // ------------------- submit handler -------------------
  // Browser back-forward cache (bfcache) reset. If the customer clicks
  // a link that navigates the current tab (e.g. "View cart" → /cart),
  // then hits the browser back button, the browser may restore this
  // page from cache with the SAME JS state — including a disabled
  // sendBtn left over from the in-flight fetch that got cancelled by
  // the navigation. `pageshow` with persisted=true is the signal that
  // we're coming back from bfcache; reset to a usable state.
  window.addEventListener("pageshow", function (e) {
    if (e.persisted) {
      sendBtn.disabled = false;
      // Drop any typing-dots placeholder left over from an interrupted
      // stream so the customer doesn't see indicators that go nowhere.
      const stub = chatEl.querySelector(".seymour-bot:last-child");
      if (stub && stub.querySelector(".seymour-typing")) stub.remove();
    }
  });

  formEl.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    checkHealth();
    const text = inputEl.value.trim();
    if (!text) return;
    inputEl.value = "";
    sendBtn.disabled = true;
    let botBubble = null;
    let buffered = "";

    // Helper called whenever a non-text SSE event arrives (tool call,
    // product cards, download link, etc.). Marks the buffered text
    // so the next 'text' delta starts on a new paragraph — prevents
    // run-ons like "now.Done — I've sent..." where text streamed
    // before and after a tool call gets concatenated mid-sentence.
    function markBotSegmentBoundary() {
      if (buffered && !buffered.endsWith("\n\n")) {
        buffered += "\n\n";
      }
    }
    try {
      appendMsg(text, "seymour-user");
      // Customer is talking now — dismiss the floating greeting bubble
      // so it doesn't compete with the conversation visually.
      const _greet = panel.querySelector(".seymour-greeting-bubble");
      if (_greet) _greet.classList.remove("open");
      recordEntry({ kind: "user", text });
      botBubble = appendMsg("", "seymour-bot");
      // Show animated typing dots until the first text delta arrives —
      // the streaming text in the `text` case overwrites botBubble.innerHTML,
      // which replaces these dots automatically.
      botBubble.innerHTML =
        '<div class="seymour-typing"><span></span><span></span><span></span></div>';
      try {
      const body = { message: text, session_id: sessionId };
      // Snapshot the live Shopify cart so the bot can answer "what's in my
      // cart?" without an extra round-trip and so it has context when the
      // customer talks about products. Fire-and-keep-going on error —
      // the chat works fine without it.
      try {
        const cartResp = await fetch("/cart.js", { credentials: "same-origin" });
        if (cartResp.ok) body.cart = await cartResp.json();
      } catch (_) { /* ignore */ }
      // Pass the signed customer identity if Liquid rendered one (logged-in
      // customers only). The API verifies the HMAC before trusting any of it.
      // For B2B customers the company context is signed too — the Acumatica
      // CustomerID (= Shopify Company.externalId) is the load-bearing field
      // for trade pricing / invoices / statements.
      if (CFG.customerId && CFG.authToken && CFG.authTs) {
        body.customer_id           = CFG.customerId;
        body.customer_email        = CFG.customerEmail;
        body.company_id            = CFG.companyId           || "";
        body.company_location_id   = CFG.companyLocationId   || "";
        body.acumatica_customer_id = CFG.acumaticaCustomerId || "";
        body.auth_token            = CFG.authToken;
        body.auth_ts               = CFG.authTs;
      } else if (CFG.customerEmail) {
        // Unsigned email — server will ignore for account features, but it
        // can still flavour greetings etc. Anonymous in practice.
        body.customer_email = CFG.customerEmail;
      }

      const resp = await fetch(API_BASE + "/api/chat/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error("HTTP " + resp.status);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let leftover = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        leftover += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = leftover.indexOf("\n\n")) >= 0) {
          const chunk = leftover.slice(0, nl);
          leftover = leftover.slice(nl + 2);
          if (!chunk.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(chunk.slice(6));
            await handleEvent(payload);
          } catch (e) {
            console.warn("[seymour] bad event JSON", e);
          }
        }
      }

      async function handleEvent(ev) {
        switch (ev.type) {
          case "session":
            sessionId = ev.session_id;
            localStorage.setItem(SESSION_KEY, sessionId);
            break;
          case "text":
            buffered += ev.delta;
            // Re-render the buffered markdown each chunk. marked is fast
            // enough that even multi-paragraph tables keep up with streaming.
            botBubble.innerHTML = renderMarkdown(buffered);
            // No auto-scroll: the user's question is anchored at the top
            // of the viewport from appendMsg(); the reply streams in
            // below so they read top-down.
            break;
          case "tool_use":
            markBotSegmentBoundary();
            appendTool(ev.name);
            // Tool chips are transient — not persisted to history
            break;
          case "product_results":
            markBotSegmentBoundary();
            appendProductCards(ev.results, { label: ev.label });
            recordEntry({ kind: "cards", products: ev.results, label: ev.label });
            break;
          case "content_results":
            markBotSegmentBoundary();
            appendContentCards(ev.results);
            recordEntry({ kind: "content", items: ev.results });
            break;
          case "cart_add":
            markBotSegmentBoundary();
            await handleCartAdd(ev);
            // handleCartAdd records its own entry
            break;
          case "download_link":
            markBotSegmentBoundary();
            appendDownloadLink(ev);
            recordEntry({
              kind: "download",
              url: ev.url,
              label: ev.label,
              expires_in_seconds: ev.expires_in_seconds,
            });
            break;
          case "abandoned_cart_tile":
            markBotSegmentBoundary();
            appendAbandonedCartTile(ev);
            recordEntry({ kind: "abandoned_cart_tile", data: ev });
            break;
          case "shipment_tile":
            markBotSegmentBoundary();
            appendShipmentTile(ev);
            recordEntry({ kind: "shipment_tile", data: ev });
            break;
          case "card_payment_modal":
            markBotSegmentBoundary();
            openEwayModal({
              intent_token:        ev.intent_token,
              amount:              ev.amount,
              summary:             ev.summary,
              eway_env:            ev.eway_env,
              eway_public_key:     ev.eway_public_key,
              card_holder_default: ev.card_holder_default,
            });
            // Don't persist the modal state — it's transient and the
            // server-side result event already handles confirmation.
            break;
          case "delivery_choice": {
            markBotSegmentBoundary();
            const data = {
              doc_type: ev.doc_type,
              doc_id:   ev.doc_id,
              label:    ev.label,
            };
            appendDeliveryChoice(data);
            recordEntry({ kind: "delivery_choice", data });
            break;
          }
          case "error":
            botBubble.classList.add("seymour-err");
            botBubble.textContent = ev.message || "Something went wrong.";
            recordEntry({ kind: "err", text: botBubble.textContent });
            break;
        }
      }
      // Record the final bot reply (if it actually produced text)
      if (buffered) {
        recordEntry({ kind: "bot", text: buffered });
      }
      } catch (err) {
        // Inner catch — fetch / stream errors. botBubble was rendered, so
        // we have something to attach the error to.
        if (botBubble) {
          botBubble.classList.add("seymour-err");
          botBubble.textContent = "Error: " + err.message;
        }
        recordEntry({ kind: "err", text: "Error: " + err.message });
      }
    } catch (outerErr) {
      // Outer catch — anything that blew up before the fetch started
      // (token prompt, DOM ops, persistHistory quota errors that
      // somehow escape). Make sure the customer sees something rather
      // than a frozen send button.
      console.error("[seymour] submit failed", outerErr);
      try {
        const errBubble = botBubble || appendMsg("Something went wrong — try again.", "seymour-bot");
        errBubble.classList.add("seymour-err");
      } catch (_) { /* DOM ops are last-ditch; don't double-throw */ }
    } finally {
      // ALWAYS re-enable the send button. This was the source of the
      // "stuck input" bug — an early throw before the inner try meant
      // sendBtn stayed disabled until full page refresh.
      sendBtn.disabled = false;
      try { inputEl.focus(); } catch (_) {}

      // Response is done — collapse the dynamic padding-bottom we added
      // on submit (to make scroll-room for the question anchor) back to
      // a small value. Otherwise the empty space below the content stays
      // scrollable indefinitely. If the user is already scrolled past
      // any newly-removed space, browser clamps to the new max scroll
      // (which is just the natural end of content). Read traffic above
      // is undisturbed because their scrollTop sits below the new max.
      try { chatEl.style.paddingBottom = ""; } catch (_) {}
    }
  });
})();
